import React from 'react';
// @ts-ignore
import { usePage, useContentToken, EditableToken } from '@vibesites/runtime/react';
import type { ComponentContentSchema } from '@vibesites/runtime';
import { CorporateFooter } from './CorporateFooter';
import { PropertyPageFooter } from './PropertyPageFooter';

/**
 * Smart footer that renders PropertyPageFooter on property template pages,
 * and CorporateFooter on all other pages.
 */
export function SmartFooter() {
  const page = usePage();
  const alias = page?.alias || '';
  const isPropertyPage = alias.startsWith('property-')
    || page?.template === 'property'
    || /^apartments\/[^/]+\/[^/]+\/[^/]+/.test(alias);

  return (
    <EditableToken path="footer.company_logo" type="image" label="Company Logo" as="div" style={{ display: 'contents' }} defaultField={contentSchema.tokens['footer.company_logo'].defaultField} defaultValue={contentSchema.tokens['footer.company_logo'].defaultValue}>
      <EditableToken path="footer.company_logo_alt" type="text" label="Company Logo Alt Text" as="div" style={{ display: 'contents' }} defaultField={contentSchema.tokens['footer.company_logo_alt'].defaultField} defaultValue={contentSchema.tokens['footer.company_logo_alt'].defaultValue}>
        <EditableToken path="footer.property_logo" type="image" label="Property Logo" as="div" style={{ display: 'contents' }} defaultField={contentSchema.tokens['footer.property_logo'].defaultField} defaultValue={contentSchema.tokens['footer.property_logo'].defaultValue}>
          <EditableToken path="footer.property_logo_alt" type="text" label="Property Logo Alt Text" as="div" style={{ display: 'contents' }} defaultField={contentSchema.tokens['footer.property_logo_alt'].defaultField} defaultValue={contentSchema.tokens['footer.property_logo_alt'].defaultValue}>
            <EditableToken path="footer.company_name" type="text" label="Company Name" as="div" style={{ display: 'contents' }} defaultValue={contentSchema.tokens['footer.company_name'].defaultValue}>
                <EditableToken path="footer.address" type="text" label="Address" as="div" style={{ display: 'contents' }} defaultField={contentSchema.tokens['footer.address'].defaultField} defaultValue={contentSchema.tokens['footer.address'].defaultValue}>
                  <EditableToken path="footer.phone" type="phone" label="Phone" as="div" style={{ display: 'contents' }} defaultField={contentSchema.tokens['footer.phone'].defaultField} defaultValue={contentSchema.tokens['footer.phone'].defaultValue}>
                    <EditableToken path="footer.copyright" type="text" label="Copyright" as="div" style={{ display: 'contents' }} defaultValue={contentSchema.tokens['footer.copyright'].defaultValue}>
                      <EditableToken path="footer.facebook_url" type="url" label="Facebook URL" as="div" style={{ display: 'contents' }} defaultField={contentSchema.tokens['footer.facebook_url'].defaultField} defaultValue={contentSchema.tokens['footer.facebook_url'].defaultValue}>
                        <EditableToken path="footer.instagram_url" type="url" label="Instagram URL" as="div" style={{ display: 'contents' }} defaultField={contentSchema.tokens['footer.instagram_url'].defaultField} defaultValue={contentSchema.tokens['footer.instagram_url'].defaultValue}>
                          <EditableToken path="footer.tiktok_url" type="url" label="TikTok URL" as="div" style={{ display: 'contents' }} defaultField={contentSchema.tokens['footer.tiktok_url'].defaultField} defaultValue={contentSchema.tokens['footer.tiktok_url'].defaultValue}>
                            {isPropertyPage ? <PropertyPageFooter /> : <CorporateFooter />}
                          </EditableToken>
                        </EditableToken>
                      </EditableToken>
                    </EditableToken>
                  </EditableToken>
                </EditableToken>
              </EditableToken>
            </EditableToken>
          </EditableToken>
        </EditableToken>
      </EditableToken>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'Footer',
  displayName: 'Footer',
  tokens: {
    'footer.company_logo': {
      label: 'Company Logo',
      type: 'image',
      defaultField: 'property_company_logo',
      suggestedFields: ['property_company_logo', 'property_company_mono_black_logo', 'property_company_mono_white_logo'],
    },
    'footer.company_logo_alt': {
      label: 'Company Logo Alt Text',
      type: 'text',
      defaultField: 'property_company_name',
      defaultValue: 'Case and Associates logo',
    },
    'footer.property_logo': {
      label: 'Property Logo',
      type: 'image',
      defaultField: 'property_primary_logo',
      suggestedFields: ['property_primary_logo', 'property_mono_white_logo', 'property_secondary_logo'],
    },
    'footer.property_logo_alt': {
      label: 'Property Logo Alt Text',
      type: 'text',
      defaultField: 'property_name',
      defaultValue: 'Property logo',
    },
    'footer.company_name': {
      label: 'Company Name',
      type: 'text',
      defaultValue: 'Case & Associates Properties, Inc.',
    },
    'footer.address': {
      label: 'Address',
      type: 'text',
      defaultField: 'property_full_address',
      defaultValue: '4200 E. Skelly Dr. Suite 800, Tulsa, OK 74135',
    },
    'footer.phone': {
      label: 'Phone',
      type: 'phone',
      defaultField: 'property_phone',
      defaultValue: '(918) 492-1983',
    },
    'footer.copyright': {
      label: 'Copyright',
      type: 'text',
      defaultValue: `Copyright © ${new Date().getFullYear()} Case and Associates`,
    },
    'footer.facebook_url': {
      label: 'Facebook URL',
      type: 'url',
      defaultField: 'property_facebook_url',
      defaultValue: 'https://www.facebook.com/CaseAndAssociates',
    },
    'footer.instagram_url': {
      label: 'Instagram URL',
      type: 'url',
      defaultField: 'property_instagram_url',
      defaultValue: 'https://www.instagram.com/caseandassociates_/',
    },
    'footer.tiktok_url': {
      label: 'TikTok URL',
      type: 'url',
      defaultField: 'property_tiktok_url',
      defaultValue: 'https://www.tiktok.com/@caseandassociates',
    },
  },
};

(SmartFooter as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default SmartFooter;
